import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Paper,
  TextField,
  MenuItem,
  IconButton,
  Typography,
  Grid,
  Alert,
  Chip,
  Tooltip,
  useMediaQuery,
  useTheme,
  Fab,
  Card,
  CardContent,
  Stack,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { Add, Edit, Delete, CheckCircle, Cancel, Payment, Send, HourglassEmpty, HourglassBottom, ThumbUp, Warning, Receipt, ReceiptLong, ExpandMore, ExpandLess, CalendarMonth, Settings, MoneyOff } from '@mui/icons-material';

import {
  getTimeEntries,
  createTimeEntry,
  updateTimeEntry,
  deleteTimeEntry,
  getEmployees,
  getCompanies,
  getAffiliates,
  getDepartments,
  getDepartmentSettings,
  markEntryPaid,
  markEntriesPaid,
  markEntryInvoiced,
  submitTimeEntry,
  submitTimeEntries,
  getShiftSettings,
  getPayPeriodPresets,
} from '../api';
import api from '../api';
import ResponsiveTable from './ResponsiveTable';
import { useConfirmDialog } from './ConfirmDialog';
import { useAuth } from '../context/AuthContext';
import { usePipeline } from '../context/PipelineContext';
import { useTerms } from '../context/TerminologyContext';
import WorkflowGuide from './WorkflowGuide';
import { formatTimeDisplay } from '../utils/formatTime';
import { formatCurrency, formatPay, formatHours } from '../utils/formatUtils';
import { buildPresetsFromApi } from '../utils/payPeriodUtils';
import TimeEntryFormDialog from './dialogs/TimeEntryFormDialog';
import EntryPaymentDialog from './dialogs/EntryPaymentDialog';
import HoursSettingsDialog from './dialogs/HoursSettingsDialog';

// Helper to format date without timezone conversion
const formatDateDisplay = (dateStr) => {
  if (!dateStr) return '';
  const datePart = dateStr.split('T')[0]; // Get just YYYY-MM-DD
  const [year, month, day] = datePart.split('-');
  return `${month}/${day}/${year}`;
};

// Helper to get date string for form input
const getDateForInput = (dateStr) => {
  if (!dateStr) return '';
  return dateStr.split('T')[0]; // Get just YYYY-MM-DD
};

export default function TimesheetEntries() {
  const { user, isAdmin, hasPermission } = useAuth();
  const { t } = useTerms();
  const { approvalEnabled } = usePipeline();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [entries, setEntries] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [departmentsEnabled, setDepartmentsEnabled] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentEntry, setCurrentEntry] = useState(null);
  const [error, setError] = useState('');
  const [selectedEntries, setSelectedEntries] = useState([]);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentEntry, setPaymentEntry] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [shiftSettings, setShiftSettings] = useState({ minimum_entry_minutes: 0, time_rounding_minutes: 0, time_format: '12h' });
  const [payPeriodPresets, setPayPeriodPresets] = useState([]);

  // Hours summary data (from /entries/employee/:id/hours endpoint)
  const [hoursData, setHoursData] = useState(null);
  const [approvalFilter, setApprovalFilter] = useState('all');
  const [summaryExpanded, setSummaryExpanded] = useState(true);

  // Settings (shared localStorage key with EmployeeHoursLookup)
  const defaultSettings = {
    showTotalHours: true,
    showOvertimeHours: true,
    showTotalPay: true,
    showTotalUnpaid: true,
    showWeeklyBreakdown: false,
    showSummaryStrip: true,
  };

  const [settings, setSettings] = useState(() => {
    try {
      const saved = localStorage.getItem('employeeHoursSettings');
      return saved ? { ...defaultSettings, ...JSON.parse(saved) } : defaultSettings;
    } catch {
      return defaultSettings;
    }
  });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [tempSettings, setTempSettings] = useState(settings);

  const handleOpenSettings = () => {
    setTempSettings(settings);
    setSettingsOpen(true);
  };

  const handleSaveSettings = () => {
    setSettings(tempSettings);
    localStorage.setItem('employeeHoursSettings', JSON.stringify(tempSettings));
    setSettingsOpen(false);
  };

  const [filters, setFilters] = useState({
    employee_id: user?.employeeId || '',
    start_date: '',
    end_date: ''
  });

  const [formData, setFormData] = useState({
    employee_id: '',
    company_id: '',
    affiliate_id: '',
    department_id: '',
    entry_date: new Date().toISOString().split('T')[0],
    start_time: '09:00',
    end_time: '17:00',
    break_minutes: 0,
    hourly_rate: 25.00,
    description: '',
    work_details: '',
    manager_notes: '',
    billable: true,
    entry_type: 'regular',
    bonus_amount: 0,
    pay_basis: ''
  });

  const [formErrors, setFormErrors] = useState({});

  // Confirm dialog hook
  const { showConfirm, ConfirmDialogComponent } = useConfirmDialog();

  // All roles see only their own entries - no employee filter needed
  const canFilterEmployees = false;
  // Admins only on this screen, and only with the server's Mark paid permission
  // Every entry here is the caller's own. Only a built-in tenant admin may mark
  // their own entries paid/invoiced (server decisionScopeIds); its flags are all true.
  const canManagePaid = user?.role === 'admin';

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const loadData = async () => {
    try {
      const [entriesRes, employeesRes, companiesRes, affiliatesRes, deptSettingsRes, shiftSettingsRes, presetsRes] = await Promise.all([
        getTimeEntries(filters),
        getEmployees(),
        getCompanies(),
        getAffiliates(),
        getDepartmentSettings().catch(() => ({ data: { departments_enabled: false } })),
        getShiftSettings().catch(() => ({ data: { minimum_entry_minutes: 0, time_rounding_minutes: 0 } })),
        getPayPeriodPresets().catch(() => ({ data: [] }))
      ]);
      setPayPeriodPresets(buildPresetsFromApi(presetsRes.data));
      setEntries(entriesRes.data);
      setEmployees(employeesRes.data);
      setCompanies(companiesRes.data);
      setAffiliates(affiliatesRes.data);
      if (shiftSettingsRes.data) {
        setShiftSettings(shiftSettingsRes.data);
      }

      const deptEnabled = !!deptSettingsRes.data.departments_enabled;
      setDepartmentsEnabled(deptEnabled);
      if (deptEnabled) {
        try {
          const deptsRes = await getDepartments();
          setDepartments(deptsRes.data);
        } catch (e) {
          console.error('Failed to load departments:', e);
        }
      }

      // Always set the logged-in user's employee_id (all roles see only their own
      // entries). Match on user.employeeId — for admins getEmployees() returns the
      // whole tenant, so [0] is not necessarily the logged-in user's record.
      const userEmployee = employeesRes.data.find(e => e.id === user?.employeeId)
        || (employeesRes.data.length === 1 ? employeesRes.data[0] : null);
      if (userEmployee) {
        setFormData(prev => ({
          ...prev,
          employee_id: userEmployee.id,
          hourly_rate: userEmployee.hourly_rate,
          department_id: userEmployee.default_department_id || ''
        }));

        // Load hours summary data for the summary strip and weekly breakdown
        try {
          const hoursParams = {};
          if (filters.start_date) hoursParams.start_date = filters.start_date;
          if (filters.end_date) hoursParams.end_date = filters.end_date;
          if (filters.include_unpaid_before) hoursParams.include_unpaid_before = filters.include_unpaid_before;
          const hoursRes = await api.get(`/entries/employee/${userEmployee.id}/hours`, { params: hoursParams });
          setHoursData(hoursRes.data);
        } catch (e) {
          // Non-critical — summary/breakdown just won't show
          console.error('Failed to load hours summary:', e);
        }
      } else {
        // No linked employee record for this login — hide the summary strip
        // rather than showing another employee's totals.
        setHoursData(null);
      }
    } catch (err) {
      setError('Failed to load data');
      console.error(err);
    }
  };

  const handleOpenDialog = (entry = null) => {
    if (entry) {
      setEditMode(true);
      setCurrentEntry(entry);
      setFormData({
        employee_id: entry.employee_id,
        company_id: entry.company_id || '',
        affiliate_id: entry.affiliate_id || '',
        department_id: entry.department_id || '',
        entry_date: getDateForInput(entry.entry_date),
        start_time: entry.start_time,
        end_time: entry.end_time,
        break_minutes: entry.break_minutes,
        hourly_rate: entry.hourly_rate,
        description: entry.description || '',
        work_details: entry.work_details || '',
        manager_notes: entry.manager_notes || '',
        billable: entry.billable !== false,
        pay_basis: entry.pay_basis || ''
      });
    } else {
      setEditMode(false);
      setCurrentEntry(null);
      const defaultEmployee = employees[0];
      setFormData({
        employee_id: defaultEmployee?.id || '',
        company_id: '',
        affiliate_id: '',
        department_id: defaultEmployee?.default_department_id || '',
        entry_date: new Date().toISOString().split('T')[0],
        start_time: '09:00',
        end_time: '17:00',
        break_minutes: 0,
        hourly_rate: defaultEmployee?.hourly_rate || 25.00,
        description: '',
        work_details: '',
        manager_notes: '',
        billable: true,
        pay_basis: ''
      });
    }
    setFormErrors({});
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setError('');
    setFormErrors({});
  };

  const [selectedPeriod, setSelectedPeriod] = useState('');

  const handlePeriodChange = (e) => {
    const value = e.target.value;
    setSelectedPeriod(value);
    const preset = payPeriodPresets.find(p => p.value === value);
    if (preset) {
      const { start, end } = preset.getValue();
      setFilters(prev => ({
        ...prev,
        start_date: start,
        end_date: end,
        include_unpaid_before: preset.includeUnpaidPrevious && start ? start : '',
      }));
    }
  };

  // Calculate total shift minutes for validation
  const calculateShiftMinutes = (startTime, endTime) => {
    if (!startTime || !endTime) return 0;
    const [startH, startM] = startTime.split(':').map(Number);
    const [endH, endM] = endTime.split(':').map(Number);
    let startMinutes = startH * 60 + startM;
    let endMinutes = endH * 60 + endM;
    // Handle overnight shifts
    if (endMinutes <= startMinutes) {
      endMinutes += 24 * 60;
    }
    return endMinutes - startMinutes;
  };

  // Validate a single field
  const validateField = (name, value, allData) => {
    const data = { ...allData, [name]: value };

    switch (name) {
      case 'start_time':
      case 'end_time': {
        if (!data.start_time || !data.end_time) return '';
        const shiftMinutes = calculateShiftMinutes(data.start_time, data.end_time);
        if (shiftMinutes <= 0) {
          return name === 'end_time' ? 'End time must be after start time' : '';
        }
        if (shiftMinutes > 24 * 60) {
          return 'Shift cannot exceed 24 hours';
        }
        // Also validate break doesn't exceed shift
        if (data.break_minutes && parseInt(data.break_minutes) >= shiftMinutes) {
          return name === 'end_time' ? 'Break exceeds shift duration' : '';
        }
        return '';
      }
      case 'break_minutes': {
        const breakMins = parseInt(value) || 0;
        if (breakMins < 0) {
          return 'Break cannot be negative';
        }
        if (data.start_time && data.end_time) {
          const shiftMinutes = calculateShiftMinutes(data.start_time, data.end_time);
          if (breakMins >= shiftMinutes) {
            return 'Break cannot exceed shift duration';
          }
        }
        if (breakMins > 480) {
          return 'Break seems too long (max 8 hours)';
        }
        return '';
      }
      case 'hourly_rate': {
        const rate = parseFloat(value);
        if (isNaN(rate) || rate <= 0) {
          return 'Rate must be greater than 0';
        }
        if (rate > 1000) {
          return 'Rate seems unusually high';
        }
        return '';
      }
      case 'entry_date': {
        if (!value) return 'Date is required';
        return '';
      }
      default:
        return '';
    }
  };

  // Validate entire form
  const validateForm = () => {
    const errors = {};
    const fieldsToValidate = ['start_time', 'end_time', 'break_minutes', 'entry_date'];
    if (isAdmin && formData.pay_basis !== 'salary') fieldsToValidate.push('hourly_rate');

    fieldsToValidate.forEach(field => {
      const error = validateField(field, formData[field], formData);
      if (error) errors[field] = error;
    });

    // Billable entries must have a description — it's the invoice line text.
    if (formData.billable !== false && !(formData.description || '').trim()) {
      errors.description = 'Description is required for billable entries (appears on invoice)';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Real-time validation
    const error = validateField(name, value, formData);
    setFormErrors(prev => ({ ...prev, [name]: error }));

    // Clear related field errors when times change
    if (name === 'start_time' || name === 'end_time') {
      const otherField = name === 'start_time' ? 'end_time' : 'start_time';
      const otherError = validateField(otherField, formData[otherField], { ...formData, [name]: value });
      const breakError = validateField('break_minutes', formData.break_minutes, { ...formData, [name]: value });
      setFormErrors(prev => ({ ...prev, [otherField]: otherError, break_minutes: breakError }));
    }

    // Update hourly rate and default department when employee changes
    if (name === 'employee_id') {
      const employee = employees.find(emp => emp.id === parseInt(value));
      if (employee) {
        setFormData(prev => ({
          ...prev,
          hourly_rate: employee.hourly_rate,
          pay_basis: '',
          department_id: employee.default_department_id || prev.department_id
        }));
      }
    }
  };

  // Open payment dialog for an entry
  const handleOpenPaymentDialog = (entry) => {
    if (entry.pay_hidden) return;
    setPaymentEntry(entry);
    setPaymentAmount(parseFloat(entry.paid_amount || 0).toFixed(2));
    setPaymentDialogOpen(true);
  };

  const handleClosePaymentDialog = () => {
    setPaymentDialogOpen(false);
    setPaymentEntry(null);
    setPaymentAmount('');
  };

  const handleSavePayment = async () => {
    if (!paymentEntry) return;
    try {
      const amount = parseFloat(paymentAmount) || 0;
      const totalPay = parseFloat(paymentEntry.total_pay);
      const isPaid = amount >= totalPay;
      await markEntryPaid(paymentEntry.id, isPaid, amount);
      handleClosePaymentDialog();
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update payment');
    }
  };

  const handleBulkTogglePaid = async (isPaid) => {
    if (selectedEntries.length === 0) return;
    try {
      await markEntriesPaid(selectedEntries, isPaid);
      setSelectedEntries([]);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update paid status');
    }
  };

  // Toggle invoiced status for a single entry
  const handleToggleInvoiced = async (entry) => {
    try {
      await markEntryInvoiced(entry.id, !entry.is_invoiced);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update invoiced status');
    }
  };

  // Submit single entry for approval
  const handleSubmitForApproval = async (entry) => {
    try {
      await submitTimeEntry(entry.id);
      loadData();
    } catch (err) {
      if (err.response?.data?.code === 'BELOW_MINIMUM') {
        setError(err.response.data.error);
      } else {
        setError(err.response?.data?.error || 'Failed to submit entry for approval');
      }
    }
  };

  // Bulk submit entries for approval
  const handleBulkSubmitForApproval = async () => {
    if (selectedEntries.length === 0) return;
    try {
      const response = await submitTimeEntries(selectedEntries);
      setSelectedEntries([]);
      loadData();
      if (response.data.skipped?.length > 0) {
        setError(`Submitted ${response.data.count} entries. ${response.data.skipped.length} entries skipped.`);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit entries for approval');
    }
  };

  // Helper to get payment status chip (3 states: Paid, Partial, Unpaid).
  // Pass `onClick` undefined for read-only mode (employees viewing their own
  // entries) — the chip still renders so the user can see whether they were
  // paid, but with no click handler / cursor / "click to modify" hint.
  const getPaymentStatusChip = (entry, onClick) => {
    if (entry.pay_hidden) {
      return (
        <Tooltip title="Amounts hidden — needs the View pay rates permission">
          <Chip
            size="small"
            label={entry.is_paid ? 'Paid' : 'Unpaid'}
            color={entry.is_paid ? 'success' : 'default'}
          />
        </Tooltip>
      );
    }
    const totalPay = parseFloat(entry.total_pay || 0);
    const paidAmount = parseFloat(entry.paid_amount || 0);
    const interactive = typeof onClick === 'function';
    const chipSx = interactive ? { cursor: 'pointer' } : undefined;

    if (paidAmount >= totalPay && totalPay > 0) {
      return (
        <Tooltip title={interactive ? 'Fully paid - click to modify' : 'Fully paid'}>
          <Chip
            size="small"
            icon={<CheckCircle />}
            label="Paid"
            color="success"
            onClick={onClick}
            sx={chipSx}
          />
        </Tooltip>
      );
    } else if (paidAmount > 0) {
      return (
        <Tooltip title={interactive
          ? `$${paidAmount.toFixed(2)} of $${totalPay.toFixed(2)} paid - click to modify`
          : `$${paidAmount.toFixed(2)} of $${totalPay.toFixed(2)} paid`}>
          <Chip
            size="small"
            icon={<HourglassBottom />}
            label={`$${paidAmount.toFixed(2)}/$${totalPay.toFixed(2)}`}
            color="warning"
            onClick={onClick}
            sx={chipSx}
          />
        </Tooltip>
      );
    } else {
      return (
        <Tooltip title={interactive ? 'Unpaid - click to record payment' : 'Unpaid'}>
          <Chip
            size="small"
            icon={<Payment />}
            label="Unpaid"
            color="default"
            onClick={onClick}
            sx={chipSx}
          />
        </Tooltip>
      );
    }
  };

  // Helper to get invoiced status chip
  const getInvoicedStatusChip = (entry, onClick) => {
    if (entry.is_invoiced) {
      return (
        <Tooltip title="Invoiced - click to unmark">
          <Chip
            size="small"
            icon={<Receipt />}
            label="Invoiced"
            color="success"
            onClick={onClick}
            sx={{ cursor: 'pointer' }}
          />
        </Tooltip>
      );
    }
    return (
      <Tooltip title="Not invoiced - click to mark as invoiced">
        <Chip
          size="small"
          icon={<ReceiptLong />}
          label="Not Invoiced"
          color="default"
          onClick={onClick}
          sx={{ cursor: 'pointer' }}
        />
      </Tooltip>
    );
  };

  // Helper to get approval status chip
  const getApprovalStatusChip = (entry, onClick) => {
    const status = entry.approval_status;
    
    // If approved, show green chip (no action)
    if (status === 'approved') {
      return (
        <Tooltip title="Approved">
          <Chip
            size="small"
            icon={<ThumbUp />}
            label="Approved"
            color="success"
          />
        </Tooltip>
      );
    }
    
    // If pending, show yellow chip (waiting for approval)
    if (status === 'pending') {
      return (
        <Tooltip title="Pending manager approval">
          <Chip
            size="small"
            icon={<HourglassEmpty />}
            label="Pending"
            color="warning"
          />
        </Tooltip>
      );
    }
    
    // If returned/denied (has approval_notes but null status), show red with tooltip
    if (entry.approval_notes && !status) {
      return (
        <Tooltip title={`Returned: ${entry.approval_notes}`}>
          <Chip
            size="small"
            icon={<Warning />}
            label="Returned"
            color="error"
            onClick={onClick}
            sx={{ cursor: onClick ? 'pointer' : 'default' }}
          />
        </Tooltip>
      );
    }
    
    // Draft (null status, no notes) - can be submitted unless below minimum
    if (isBelowMinimum(entry)) {
      const minMin = shiftSettings.minimum_entry_minutes || 0;
      return (
        <Tooltip title={`Below ${minMin}-minute minimum. Adjust before submitting.`}>
          <Chip
            size="small"
            icon={<Warning />}
            label="Below Min"
            color="error"
            variant="outlined"
          />
        </Tooltip>
      );
    }
    return (
      <Tooltip title="Click to submit for approval">
        <Chip
          size="small"
          icon={<Send />}
          label="Draft"
          variant="outlined"
          onClick={onClick}
          sx={{ cursor: onClick ? 'pointer' : 'default' }}
        />
      </Tooltip>
    );
  };

  // Check if entry can be edited (draft, returned, or pending if owned by current user)
  const canEditEntry = (entry) => {
    // When approval stage is disabled, owners can always edit their own entries
    // (no approval gate exists, so "approved" isn't a lock)
    if (!approvalEnabled && user?.employeeId && entry.employee_id === user.employeeId) {
      return true;
    }
    // Draft entries (no approval status) can always be edited
    if (!entry.approval_status || entry.approval_status === null) {
      return true;
    }
    // Pending entries can be edited by the employee who owns them
    // Use == for loose equality to handle number/string type mismatch
    if (entry.approval_status === 'pending' && user?.employeeId && entry.employee_id === user.employeeId) {
      return true;
    }
    // Approved entries cannot be edited by employees (admin/manager bypass handled separately)
    return false;
  };

  // Check if entry can be submitted (must be draft and above minimum time)
  const canSubmitEntry = (entry) => {
    if (entry.approval_status && entry.approval_status !== null) return false;
    const minMinutes = shiftSettings.minimum_entry_minutes || 0;
    if (minMinutes > 0 && parseFloat(entry.hours_worked) * 60 < minMinutes) return false;
    return true;
  };

  // Check if entry is below minimum time threshold
  const isBelowMinimum = (entry) => {
    const minMinutes = shiftSettings.minimum_entry_minutes || 0;
    return minMinutes > 0 && parseFloat(entry.hours_worked) * 60 < minMinutes;
  };

  // Filter entries by approval status
  const filteredEntries = useMemo(() => {
    if (approvalFilter === 'all') return entries;
    if (approvalFilter === 'draft') return entries.filter(e => !e.approval_status);
    return entries.filter(e => e.approval_status === approvalFilter);
  }, [entries, approvalFilter]);

  const handleSubmit = async () => {
    // Validate form before submitting
    if (!validateForm()) {
      setError('Please fix the validation errors before saving');
      return;
    }

    try {
      if (editMode) {
        await updateTimeEntry(currentEntry.id, formData);
      } else {
        await createTimeEntry(formData);
      }
      handleCloseDialog();
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save entry');
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    const confirmed = await showConfirm({
      title: 'Delete Entry',
      message: 'Are you sure you want to delete this time entry? This action cannot be undone.',
      confirmText: 'Delete',
      confirmColor: 'error',
      severity: 'warning'
    });

    if (confirmed) {
      try {
        await deleteTimeEntry(id);
        loadData();
      } catch (err) {
        setError('Failed to delete entry');
        console.error(err);
      }
    }
  };

  // Define columns for ResponsiveTable
  const columns = [
    {
      field: 'entry_date',
      headerName: 'Date',
      mobilePrimary: true,
      renderCell: ({ row }) => formatDateDisplay(row.entry_date),
    },
    {
      field: 'employee_name',
      headerName: 'Employee',
      mobileSecondary: true,
      renderCell: ({ row }) => `${row.first_name} ${row.last_name}`,
    },
    {
      field: 'company_name',
      headerName: t('client.singular'),
      mobileHidden: true,
      renderCell: ({ row }) => row.company_name || 'N/A',
    },
    ...(departmentsEnabled ? [{
      field: 'department_name',
      headerName: t('department.singular') === 'Department' ? 'Dept' : t('department.singular'),
      mobileHidden: true,
      renderCell: ({ row }) => row.department_name || '—',
    }] : []),
    {
      field: 'start_time',
      headerName: 'Start',
      mobileHidden: true,
      renderCell: ({ row }) => formatTimeDisplay(row.start_time, shiftSettings.time_format),
    },
    {
      field: 'end_time',
      headerName: 'End',
      mobileHidden: true,
      renderCell: ({ row }) => formatTimeDisplay(row.end_time, shiftSettings.time_format),
    },
    {
      field: 'hours_worked',
      headerName: 'Hours',
      align: 'right',
      renderCell: ({ row }) => parseFloat(row.hours_worked).toFixed(2),
    },
    {
      field: 'total_pay',
      headerName: 'Pay',
      align: 'right',
      renderCell: ({ row }) => (row.pay_basis === 'salary'
        ? <Tooltip title="Covered by salary — billed to the client, not paid separately"><Chip size="small" label="Salary" variant="outlined" /></Tooltip>
        : formatPay(row.total_pay)),
    },
    ...(approvalEnabled ? [{
      field: 'approval_status',
      headerName: 'Approval',
      renderCell: ({ row }) => getApprovalStatusChip(row, canSubmitEntry(row) ? (e) => {
        e.stopPropagation();
        handleSubmitForApproval(row);
      } : null),
    }] : []),
    {
      field: 'is_paid',
      headerName: 'Payment',
      // Always render so employees can see whether their entry is paid.
      // Click-to-record-payment is gated to admins.
      renderCell: ({ row }) => getPaymentStatusChip(row, canManagePaid ? (e) => {
        e.stopPropagation();
        handleOpenPaymentDialog(row);
      } : undefined),
    },
    ...(canManagePaid ? [{
      field: 'is_invoiced',
      headerName: 'Invoiced',
      renderCell: ({ row }) => getInvoicedStatusChip(row, (e) => {
        e.stopPropagation();
        handleToggleInvoiced(row);
      }),
    }] : []),
    {
      field: 'description',
      headerName: 'Description',
      mobileHidden: true,
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          {row.description && (
            <Tooltip title={row.description}>
              <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                {row.description}
              </Typography>
            </Tooltip>
          )}
          {row.manager_notes && (
            <Tooltip title={`Manager: ${row.manager_notes}`}>
              <Chip size="small" label="Mgr" color="warning" />
            </Tooltip>
          )}
          {row.work_details && (
            <Tooltip title={row.work_details}>
              <Chip size="small" label="Details" variant="outlined" />
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  // Mobile card view for entries
  const renderMobileEntries = () => (
    <Stack spacing={1.5}>
      {filteredEntries.map((entry) => {
        const isSelected = selectedEntries.includes(entry.id);
        return (
          <Card 
            key={entry.id}
            variant="outlined"
            sx={{ 
              bgcolor: entry.is_paid ? 'rgba(76, 175, 80, 0.08)' : parseFloat(entry.paid_amount || 0) > 0 ? 'rgba(255, 152, 0, 0.08)' : 'background.paper',
              border: isSelected ? '2px solid' : '1px solid',
              borderColor: isSelected ? 'primary.main' : 'divider',
            }}
          >
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
              {/* Header row */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                {canManagePaid && (
                  <Checkbox
                    checked={isSelected}
                    onChange={() => {
                      setSelectedEntries(prev => 
                        prev.includes(entry.id) 
                          ? prev.filter(e => e !== entry.id) 
                          : [...prev, entry.id]
                      );
                    }}
                    size="small"
                    sx={{ mr: 1, mt: -0.5, ml: -1 }}
                  />
                )}
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" fontWeight="medium">
                    {formatDateDisplay(entry.entry_date)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {entry.first_name} {entry.last_name}
                    {entry.company_name && ` • ${entry.company_name}`}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <IconButton
                    size="small"
                    aria-label="Edit time entry"
                    onClick={() => handleOpenDialog(entry)}
                    disabled={!canEditEntry(entry) && !isAdmin}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    aria-label="Delete time entry"
                    onClick={() => handleDelete(entry.id)}
                    disabled={!canEditEntry(entry) && !isAdmin}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              {/* Details row */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                <Chip
                  size="small"
                  label={`${formatTimeDisplay(entry.start_time, shiftSettings.time_format)} - ${formatTimeDisplay(entry.end_time, shiftSettings.time_format)}`}
                  variant="outlined"
                />
                <Chip 
                  size="small" 
                  label={`${parseFloat(entry.hours_worked).toFixed(2)} hrs`}
                  color="primary"
                  variant="outlined"
                />
                {entry.pay_basis === 'salary' ? (
                  <Tooltip title="Covered by salary — billed to the client, not paid separately">
                    <Chip size="small" label="Salary" variant="outlined" />
                  </Tooltip>
                ) : (
                  <Chip 
                    size="small" 
                    label={formatPay(entry.total_pay)}
                    color="success"
                  />
                )}
                {/* Approval Status */}
                {approvalEnabled && getApprovalStatusChip(entry, canSubmitEntry(entry) ? () => handleSubmitForApproval(entry) : null)}
                {/* Payment + Invoiced chips: shown to everyone so employees can
                    see their own paid status. Click handlers gated to admins so
                    only they can record/modify the payment. */}
                {getPaymentStatusChip(entry, canManagePaid ? () => handleOpenPaymentDialog(entry) : undefined)}
                {canManagePaid && getInvoicedStatusChip(entry, () => handleToggleInvoiced(entry))}
                {entry.work_details && (
                  <Tooltip title={entry.work_details}>
                    <Chip size="small" label="Details" variant="outlined" />
                  </Tooltip>
                )}
                {entry.manager_notes && (
                  <Tooltip title={`Manager: ${entry.manager_notes}`}>
                    <Chip size="small" label="Mgr" color="warning" />
                  </Tooltip>
                )}
              </Box>
              {entry.description && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {entry.description}
                </Typography>
              )}
            </CardContent>
          </Card>
        );
      })}
      {filteredEntries.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary">
            {approvalFilter !== 'all' ? `No ${approvalFilter} entries found` : 'No time entries found'}
          </Typography>
        </Box>
      )}
    </Stack>
  );

  // Check if user account is linked to an employee record

  if (user && !user.employeeId && user.role !== 'program_admin' && !isAdmin) {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', mt: 4 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Account Not Linked to Employee Record
          </Typography>
          <Typography variant="body2">
            Your user account is not linked to an employee record. You cannot create or view time entries until this is resolved.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Please contact your administrator to link your account to an employee record.
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ pb: isMobile ? 8 : 0 }}>
      {/* Header */}
      <Box sx={{
        mb: 2,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 1,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant={isMobile ? "h6" : "h5"}>
            My Time Entries
          </Typography>
          <WorkflowGuide showLabel={!isMobile} />
          <Tooltip title="Display settings — choose which summary cards and sections to show">
            <IconButton onClick={handleOpenSettings} size="small" sx={{ ml: 1 }}>
              <Settings fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        {!isMobile && (
          <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()}>
            Add Entry
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          {canFilterEmployees && (
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                select
                label="Filter by Employee"
                value={filters.employee_id}
                onChange={(e) => setFilters({ ...filters, employee_id: e.target.value })}
                size={isMobile ? "small" : "medium"}
                SelectProps={{ displayEmpty: true }}
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value="">All Employees</MenuItem>
                {employees.map(emp => (
                  <MenuItem key={emp.id} value={emp.id}>
                    {emp.first_name} {emp.last_name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          )}
          <Grid item xs={6} sm={canFilterEmployees ? 3 : 6} md={canFilterEmployees ? 4 : 6}>
            <TextField
              fullWidth
              type="date"
              label="Start Date"
              value={filters.start_date}
              onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
              size={isMobile ? "small" : "medium"}
            />
          </Grid>
          <Grid item xs={6} sm={canFilterEmployees ? 3 : 6} md={canFilterEmployees ? 4 : 6}>
            <TextField
              fullWidth
              type="date"
              label="End Date"
              value={filters.end_date}
              onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
              size={isMobile ? "small" : "medium"}
            />
          </Grid>
        </Grid>
        
        {/* Pay Period Quick Select + Approval Filter */}
        <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <TextField
            select
            label="Pay Period"
            value={selectedPeriod}
            onChange={handlePeriodChange}
            size={isMobile ? "small" : "medium"}
            sx={{ minWidth: 200 }}
          >
            {payPeriodPresets.map((preset) => (
              <MenuItem key={preset.value} value={preset.value} title={preset.description}>
                {preset.label}
              </MenuItem>
            ))}
          </TextField>
          {approvalEnabled && (
            <TextField
              select
              label="Approval Status"
              value={approvalFilter}
              onChange={(e) => setApprovalFilter(e.target.value)}
              size={isMobile ? "small" : "medium"}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
            </TextField>
          )}
        </Box>
      </Paper>

      {/* Period Summary Strip */}
      {hoursData && settings.showSummaryStrip && (
        <Paper sx={{ p: 1, mb: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
          <IconButton size="small" onClick={() => setSummaryExpanded(!summaryExpanded)} sx={{ p: 0.25 }}>
            {summaryExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </IconButton>
          {summaryExpanded && (() => {
            const unpaidTotal = (hoursData.entries || []).reduce((sum, e) => {
              const totalPay = parseFloat(e.total_pay || 0);
              const paidAmount = parseFloat(e.paid_amount || 0);
              return sum + Math.max(0, totalPay - paidAmount);
            }, 0);
            const anyHidden = (hoursData.entries || []).some(e => e.pay_hidden === true);
            return (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: isMobile ? 0.75 : 1.25, alignItems: 'center', fontSize: '0.75rem' }}>
                {settings.showTotalHours && (
                  <Typography variant="caption" sx={{ fontSize: '0.75rem' }}><strong>{formatHours(hoursData.summary.total_hours)}</strong> hrs</Typography>
                )}
                {settings.showOvertimeHours && hoursData.summary.overtime_hours > 0 && (
                  <Typography variant="caption" color="error" sx={{ fontSize: '0.75rem' }}><strong>{formatHours(hoursData.summary.overtime_hours)}</strong> OT</Typography>
                )}
                {settings.showTotalPay && (
                  <Typography variant="caption" sx={{ fontSize: '0.75rem' }}><strong>{formatPay(hoursData.summary.total_pay)}</strong> total</Typography>
                )}
                {settings.showTotalUnpaid && !anyHidden && unpaidTotal > 0 && (
                  <Typography variant="caption" color="error" sx={{ fontSize: '0.75rem' }}><MoneyOff sx={{ fontSize: 13, verticalAlign: 'middle', mr: 0.3 }} /><strong>{formatCurrency(unpaidTotal)}</strong> unpaid</Typography>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                  {hoursData.summary.total_entries} entries
                </Typography>
              </Box>
            );
          })()}
        </Paper>
      )}

      {/* Weekly Breakdown */}
      {hoursData && settings.showWeeklyBreakdown && Object.keys(hoursData.weeklyTotals || {}).length > 0 && (
        <Accordion defaultExpanded={false} sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant={isMobile ? 'subtitle1' : 'h6'}>
              <CalendarMonth sx={{ mr: 1, verticalAlign: 'middle', fontSize: isMobile ? 18 : 24 }} />
              Weekly Breakdown
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: isMobile ? 1 : 2 }}>
            {isMobile ? (
              <Stack spacing={1}>
                {Object.entries(hoursData.weeklyTotals).map(([week, data]) => (
                  <Card key={week} variant="outlined" sx={{ p: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Typography variant="subtitle2" fontWeight="bold">{week}</Typography>
                      {data.hours > 40 ? (
                        <Chip label={`${formatHours(data.hours - 40)} OT`} color="error" size="small" sx={{ fontSize: '0.65rem', height: 20 }} />
                      ) : (
                        <Chip label="Regular" color="success" size="small" sx={{ fontSize: '0.65rem', height: 20 }} />
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="textSecondary">
                        {formatHours(data.hours)} hrs &bull; {data.entries.length} entries
                      </Typography>
                      <Typography variant="body2" fontWeight="bold" color="primary">
                        {formatPay(data.pay)}
                      </Typography>
                    </Box>
                  </Card>
                ))}
              </Stack>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Week</TableCell>
                      <TableCell align="right">Hours</TableCell>
                      <TableCell align="right">Pay</TableCell>
                      <TableCell align="right">Entries</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(hoursData.weeklyTotals).map(([week, data]) => (
                      <TableRow key={week}>
                        <TableCell>{week}</TableCell>
                        <TableCell align="right">{formatHours(data.hours)}</TableCell>
                        <TableCell align="right">{formatPay(data.pay)}</TableCell>
                        <TableCell align="right">{data.entries.length}</TableCell>
                        <TableCell>
                          {data.hours > 40 ? (
                            <Chip label={`${formatHours(data.hours - 40)} OT`} color="error" size="small" />
                          ) : (
                            <Chip label="Regular" color="success" size="small" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </AccordionDetails>
        </Accordion>
      )}

      {/* Bulk actions */}
      {selectedEntries.length > 0 && (
        <Paper sx={{ p: 1.5, mb: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="body2">
            {selectedEntries.length} selected
          </Typography>
          {approvalEnabled && (
            <Button
              size="small"
              variant="contained"
              color="primary"
              startIcon={<Send />}
              onClick={handleBulkSubmitForApproval}
            >
              Submit for Approval
            </Button>
          )}
          {canManagePaid && (
            <>
              <Button
                size="small"
                variant="contained"
                color="success"
                startIcon={<CheckCircle />}
                onClick={() => handleBulkTogglePaid(true)}
              >
                Mark Paid
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="warning"
                startIcon={<Cancel />}
                onClick={() => handleBulkTogglePaid(false)}
              >
                Mark Unpaid
              </Button>
            </>
          )}
          <Button
            size="small"
            variant="text"
            onClick={() => setSelectedEntries([])}
          >
            Clear
          </Button>
        </Paper>
      )}

      {/* Entries List/Table */}
      {isMobile ? (
        renderMobileEntries()
      ) : (
        <ResponsiveTable
          columns={columns}
          rows={filteredEntries}
          selectable={true}
          selectedIds={selectedEntries}
          onSelectionChange={setSelectedEntries}
          actions={(row) => (
            <>
              <IconButton
                size="small"
                aria-label="Edit time entry"
                onClick={() => handleOpenDialog(row)}
                disabled={!canEditEntry(row) && !isAdmin}
              >
                <Edit />
              </IconButton>
              <IconButton
                size="small"
                aria-label="Delete time entry"
                onClick={() => handleDelete(row.id)}
                disabled={!canEditEntry(row) && !isAdmin}
              >
                <Delete />
              </IconButton>
            </>
          )}
          emptyMessage="No time entries found"
        />
      )}

      {/* Mobile FAB for adding entry */}
      {isMobile && (
        <Fab
          color="primary"
          aria-label="add"
          onClick={() => handleOpenDialog()}
          sx={{
            position: 'fixed',
            bottom: 80, // Above bottom navigation
            right: 16,
            zIndex: 1000,
          }}
        >
          <Add />
        </Fab>
      )}

      <TimeEntryFormDialog
        open={openDialog}
        onClose={handleCloseDialog}
        onSubmit={handleSubmit}
        editMode={editMode}
        formData={formData}
        formErrors={formErrors}
        handleInputChange={handleInputChange}
        setFormData={setFormData}
        canFilterEmployees={canFilterEmployees}
        employees={employees}
        companies={companies}
        affiliates={affiliates}
        departments={departments}
        departmentsEnabled={departmentsEnabled}
        isAdmin={isAdmin}
        canManagePaid={canManagePaid}
        canEditTimes={isAdmin || hasPermission('can_edit_entry_times')}
      />

      <EntryPaymentDialog
        open={paymentDialogOpen}
        onClose={handleClosePaymentDialog}
        onSave={handleSavePayment}
        paymentEntry={paymentEntry}
        paymentAmount={paymentAmount}
        setPaymentAmount={setPaymentAmount}
      />

      <HoursSettingsDialog
        open={settingsOpen}
        onCancel={() => setSettingsOpen(false)}
        onSave={handleSaveSettings}
        tempSettings={tempSettings}
        setTempSettings={setTempSettings}
        variant="employee"
      />

      {/* Confirm Dialog */}
      <ConfirmDialogComponent />
    </Box>
  );
}
